import { isArticlePath } from './resolveChannel';

export const FREESTAR_PUBLISHER = 'benzinga-com';
export const FREESTAR_INTL_HOST_LABELS = ['es', 'fr', 'it', 'jp', 'kr'] as const;
export const FREESTAR_STICKY_FOOTER_MOBILE_ENABLED = true;
export const FREESTAR_STICKY_FOOTER_PLACEMENT = 'benzinga-com_sticky_footer';
export const FREESTAR_STICKY_FOOTER_MOBILE_PLACEMENT = 'benzinga-com_sticky_footer_mobile';
export const FREESTAR_STICKY_FOOTER_MOBILE_MAX_WIDTH = 767;

type FreestarIntlHostLabel = (typeof FREESTAR_INTL_HOST_LABELS)[number];

const isFreestarIntlHostLabel = (label: string): label is FreestarIntlHostLabel =>
  (FREESTAR_INTL_HOST_LABELS as readonly string[]).includes(label);

let hostnameForTests: string | null = null;

export const setFreestarHostnameForTests = (hostname: string | null): void => {
  hostnameForTests = hostname;
};

const hostnameFromWindow = (): string =>
  hostnameForTests ?? (typeof window === 'undefined' ? '' : window.location.hostname);

export const resolveFreestarHostLabel = (hostname = hostnameFromWindow()): string =>
  hostname.split('.')[0]?.toLowerCase() ?? '';

export const isFreestarInternationalHost = (hostname = hostnameFromWindow()): boolean =>
  isFreestarIntlHostLabel(resolveFreestarHostLabel(hostname));

export const resolveFreestarPlacementPrefix = (hostname = hostnameFromWindow()): string => {
  const label = resolveFreestarHostLabel(hostname);
  return isFreestarIntlHostLabel(label) ? `${label}-${FREESTAR_PUBLISHER}` : FREESTAR_PUBLISHER;
};

export const resolveFreestarStickyFooterPlacement = (viewportWidth: number): string =>
  viewportWidth <= FREESTAR_STICKY_FOOTER_MOBILE_MAX_WIDTH
    ? FREESTAR_STICKY_FOOTER_MOBILE_PLACEMENT
    : FREESTAR_STICKY_FOOTER_PLACEMENT;

export const shouldDisableFreestarMobileStickyFooterForPath = (
  pathname: string | null | undefined,
  viewportWidth: number,
): boolean => {
  if (viewportWidth > FREESTAR_STICKY_FOOTER_MOBILE_MAX_WIDTH) {
    return false;
  }
  if (!FREESTAR_STICKY_FOOTER_MOBILE_ENABLED) return true;
  return !isArticlePath(pathname);
};

export const shouldDisableFreestarStickyFooterForPage = ({
  adsFullyDisabled,
  disableFooterDisplayAd,
  dismissed = false,
  pathname,
  suppressNonSidebarAds,
  viewportWidth,
}: {
  adsFullyDisabled: boolean;
  disableFooterDisplayAd: boolean;
  dismissed?: boolean;
  pathname?: string | null;
  suppressNonSidebarAds: boolean;
  viewportWidth?: number;
}): boolean =>
  adsFullyDisabled ||
  suppressNonSidebarAds ||
  dismissed ||
  disableFooterDisplayAd ||
  (typeof viewportWidth === 'number' && shouldDisableFreestarMobileStickyFooterForPath(pathname, viewportWidth));

export type FreestarPlacement =
  | 'top_header'
  | 'incontent'
  | 'sidebar_1'
  | 'sidebar_2'
  | 'belowcontent'
  | 'infeed'
  | 'native';

export const FREESTAR_PLACEMENTS: readonly FreestarPlacement[] = [
  'top_header',
  'incontent',
  'sidebar_1',
  'sidebar_2',
  'belowcontent',
  'infeed',
  'native',
] as const;

export const isFreestarPlacement = (type: string | null | undefined): type is FreestarPlacement =>
  typeof type === 'string' && (FREESTAR_PLACEMENTS as readonly string[]).includes(type);

export const FREESTAR_INCONTENT_MAX = 5;
export const FREESTAR_INFEED_MAX = 4;
export const FREESTAR_NATIVE_MAX = 2;

export const freestarPlacementHeightMap: Record<FreestarPlacement, string> = {
  belowcontent: 'min-h-[250px] max-h-[250px]',
  incontent: 'min-h-[250px] max-h-[250px]',
  infeed: 'min-h-[250px] max-h-[250px]',
  native: 'min-h-[250px] max-h-[250px]',
  sidebar_1: 'min-h-[250px] max-h-[250px]',
  sidebar_2: 'min-h-[250px] max-h-[250px]',
  top_header: 'min-h-[90px] max-h-[90px]',
};

const SIDEBAR_WIDTH_CLASS = 'min-w-[300px] w-[300px]';
const SPAN_WIDTH_CLASS = 'w-full';

export const hasExplicitWidthClass = (className?: string): boolean =>
  typeof className === 'string' &&
  /(?:^|\s)(?:min-|max-)?w-(?:full|screen|svw|lvw|dvw|min|max|fit|px|auto|\[|\d)/.test(className);

export const freestarPlacementWidthClass = (placement: FreestarPlacement, className?: string): string => {
  if (hasExplicitWidthClass(className)) return '';
  return placement === 'sidebar_1' || placement === 'sidebar_2' ? SIDEBAR_WIDTH_CLASS : SPAN_WIDTH_CLASS;
};

const FREESTAR_LOCALE_TAG_PLACEMENTS: Record<FreestarIntlHostLabel, readonly FreestarPlacement[]> = {
  es: ['top_header', 'incontent', 'sidebar_1', 'sidebar_2', 'belowcontent'],
  fr: ['top_header', 'incontent', 'sidebar_1', 'sidebar_2', 'belowcontent'],
  it: ['top_header', 'incontent', 'sidebar_1', 'sidebar_2', 'belowcontent'],
  jp: ['top_header', 'incontent', 'sidebar_1', 'sidebar_2', 'belowcontent'],
  kr: ['incontent', 'sidebar_1', 'sidebar_2', 'belowcontent'],
};

const freestarPlacementPrefix = (placement: FreestarPlacement, hostname?: string): string => {
  const resolvedHostname = hostname ?? hostnameFromWindow();
  const label = resolveFreestarHostLabel(resolvedHostname);
  if (!isFreestarIntlHostLabel(label) || !FREESTAR_LOCALE_TAG_PLACEMENTS[label].includes(placement)) {
    return FREESTAR_PUBLISHER;
  }
  return resolveFreestarPlacementPrefix(resolvedHostname);
};

const freestarPlacementSuffix = (placement: FreestarPlacement, index?: number): string => {
  switch (placement) {
    case 'top_header':
      return 'top_header';
    case 'belowcontent':
      return 'belowcontent';
    case 'sidebar_1':
      return 'sidebar_1';
    case 'sidebar_2':
      return 'sidebar_2';
    case 'incontent':
      return `incontent_${index ?? 1}`;
    case 'infeed':
      return `infeed_${index ?? 1}`;
    case 'native':
      return `native_${index ?? 1}`;
  }
};

export const freestarPlacementName = (placement: FreestarPlacement, index?: number, hostname?: string): string =>
  `${freestarPlacementPrefix(placement, hostname)}_${freestarPlacementSuffix(placement, index)}`;
